import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Stack, Typography, Chip, Grid, Button, Divider, Avatar,
} from '@mui/material';
import {
  Person as ProfileIcon, Logout as LogoutIcon,
  Phone as PhoneIcon, Class as ClassIcon, Badge as BadgeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// PortalProfile — student / guardian summary + sign-out. Pulled
// straight from the parent token so this page works even offline.

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 0.8 }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{value || '—'}</Typography>
      </Box>
    </Stack>
  );
}

export default function PortalProfile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const p = user?.parent || {};
  const initials = (p.student_name || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const handleLogout = () => { logout(); nav('/login', { replace: true }); };

  return (
    <Box>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
        <ProfileIcon color="primary" />
        <Typography variant="h6" fontWeight={800}>Profile</Typography>
      </Stack>

      <Grid container spacing={1.5}>
        {/* Student */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Avatar sx={{
                width: 56, height: 56, bgcolor: 'primary.main', fontWeight: 800, fontSize: 22,
              }}>{initials}</Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}>
                  Student
                </Typography>
                <Typography fontWeight={800} sx={{ fontSize: 18, lineHeight: 1.2 }}>
                  {p.student_name || '—'}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <InfoRow icon={<BadgeIcon fontSize="small" />} label="Index Number" value={p.student_ind} />
            <InfoRow icon={<ClassIcon fontSize="small" />} label="Class"        value={p.cla} />
          </Card>
        </Grid>

        {/* Guardian */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10, mb: 1 }}>
              Guardian / Parent
            </Typography>
            <Typography fontWeight={800} sx={{ fontSize: 18, lineHeight: 1.2, mb: 1 }}>
              {p.guardian || 'Not on file'}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <InfoRow icon={<PhoneIcon fontSize="small" />} label="Phone" value={user?.phone} />
            <Chip size="small" label="Update from school office" sx={{ mt: 1, height: 22 }} />
          </Card>
        </Grid>

        {/* Sign-out */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}
              justifyContent="space-between">
              <Box>
                <Typography fontWeight={800}>Sign out of the portal</Typography>
                <Typography variant="body2" color="text.secondary">
                  You'll need your PIN to sign back in. Contact the school office if you've forgotten it.
                </Typography>
              </Box>
              <Button variant="outlined" color="error" startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Sign out
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
